import type { OrderData } from '@mogul/api-client';
// @ts-expect-error: build .d.ts errors
import { Seaport } from '@opensea/seaport-js';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWalletClient } from 'wagmi';

import { WEB3_CONFIG } from '../lib';
import { walletClientToSignerV6 } from '../lib/smart-contract-utils';
import { useWeb3State } from '.';

interface CreateOrder {
  nftContractAddress: string;
  nftId: string;
  nftAmount: number;
  nftType: number;
  offerAmount: string;
  offerer: string;
  expiredAt: Date;
}

const getSeaportOrderByMogulOrder = (
  {
    nftContractAddress,
    nftId,
    nftAmount,
    nftType,
    offerAmount,
    offerer,
    expiredAt,
  }: CreateOrder,
  chainKey: keyof typeof WEB3_CONFIG.contracts,
) => ({
  offer: [
    {
      itemType: 1,
      token: WEB3_CONFIG.contracts[chainKey].stars,
      identifierOrCriteria: 0,
      amount: offerAmount,
    },
  ],
  consideration: [
    {
      itemType: nftType,
      token: nftContractAddress,
      identifier: nftId,
      amount: nftAmount,
      recipient: offerer,
    },
  ],
  counter: 0,
  startTime: moment().unix(),
  endTime: Math.round(moment(expiredAt.getTime()).unix()),
  orderType: 0,
});

export const useSeaport = () => {
  const { walletDetails } = useWeb3State();
  const walletClient = useWalletClient();

  const [signer, setSigner] =
    useState<ReturnType<typeof walletClientToSignerV6> | null>(null);

  const seaport = useMemo(() => {
    if (!signer) {
      return null;
    }

    return new Seaport(signer);
  }, [signer]);

  const createOrder = useCallback(
    async (order: CreateOrder) => {
      if (!seaport) {
        return;
      }

      const input = getSeaportOrderByMogulOrder(order, walletDetails.chain.key);

      const { executeAllActions } = await seaport.createOrder(
        input,
        order.offerer,
        true,
      );

      return executeAllActions();
    },
    [seaport, walletDetails.chain.key],
  );

  const fulfillOrder = useCallback(
    async (order: OrderData) => {
      if (!seaport) {
        return;
      }

      const { executeAllActions } = await seaport.fulfillOrder({ order });

      return executeAllActions();
    },
    [seaport],
  );

  useEffect(() => {
    if (!walletClient.data) {
      return;
    }

    const _provider = walletClientToSignerV6(walletClient.data);

    setSigner(_provider);
  }, [walletClient.data]);

  return useMemo(() => {
    return seaport
      ? {
          seaport,
          createOrder,
          fulfillOrder,
        }
      : {};
  }, [seaport, createOrder, fulfillOrder]);
};
